<template>
  <div class="honors-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchPageData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 内容区域 -->
        <div v-else class="content-wrapper">
          <!-- 富文本内容（如果有） -->
          <div
            v-if="richtextBeforeVideo || richtextAfterVideo"
            class="richtext-content"
          >
            <div v-html="richtextBeforeVideo"></div>

            <!-- 视频播放区域 -->
            <div v-if="introVideoPath" class="video-player-section">
              <div class="video-player-container">
                <div class="video-wrapper">
                  <video
                    ref="videoPlayer"
                    class="company-video"
                    :src="introVideoPath"
                    controls
                    playsinline
                    autoplay
                    preload="metadata"
                    @loadeddata="onVideoLoaded"
                    @error="onVideoError"
                    @ended="onVideoEnded"
                  >
                    <source :src="introVideoPath" type="video/mp4">
                    {{ t('video.browser_not_support') }}
                  </video>

                  <div v-if="isVideoLoading" class="video-loading">
                    <div class="loading-spinner"></div>
                    <p class="loading-text">{{ t('video.buffering') }}</p>
                  </div>

                  <!-- <div v-if="showVideoCover" class="video-cover" @click="playVideo">
                    <div class="cover-content">
                      <div class="play-button">
                        <el-icon size="80" color="white">
                          <VideoPlay />
                        </el-icon>
                      </div>
                      <p class="play-hint">{{ t('video.click_to_play') }}</p>
                    </div>
                  </div> -->
<!-- 
                  <div class="video-overlay" @click="togglePlay">
                    <div v-if="!isPlaying" class="play-overlay">
                      <div class="play-button-overlay">
                        <el-icon size="60" color="white">
                          <VideoPlay />
                        </el-icon>
                      </div>
                    </div>
                  </div> -->
                </div>
<!-- 
                <div class="video-info">
                  <div class="video-meta">
                    <div class="meta-item">
                      <el-icon><Clock /></el-icon>
                      <span>{{ videoDuration }}</span>
                    </div>
                  </div>

                  <div class="control-buttons">
                    <button
                      class="control-btn"
                      :class="{ active: isPlaying }"
                      @click="togglePlay"
                    >
                      <el-icon>
                        <component :is="isPlaying ? VideoPause : VideoPlay" />
                      </el-icon>
                      <span>{{ isPlaying ? t('video.pause') : t('video.play') }}</span>
                    </button>

                    <button class="control-btn" @click="toggleMute">
                      <el-icon>
                        <component :is="isMuted ? MuteNotification : Bell" />
                      </el-icon>
                      <span>{{ isMuted ? t('video.unmute') : t('video.mute') }}</span>
                    </button>

                    <button class="control-btn" @click="restartVideo">
                      <el-icon><Refresh /></el-icon>
                      <span>{{ t('video.replay') }}</span>
                    </button>

                    <button class="control-btn" @click="toggleFullscreen">
                      <el-icon>
                        <component :is="isFullscreen ? Close : FullScreen" />
                      </el-icon>
                      <span>{{ isFullscreen ? t('video.exit_fullscreen') : t('video.fullscreen') }}</span>
                    </button>
                  </div>
                </div> -->
              </div>
            </div>

            <div v-html="richtextAfterVideo"></div>

            <ResumeEmailDisplay />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { Warning, VideoPlay, VideoPause, Bell, MuteNotification, Refresh, FullScreen, Close, Clock } from '@element-plus/icons-vue'
import { getRecruitmentIntro } from "@/assets/api/website";
import ResumeEmailDisplay from '@/views/components/ResumeEmailDisplay.vue'
// 使用 i18n
const { locale, t } = useI18n()

// 响应式数据
const pageData = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const currentIndex = ref(0)
const autoPlayTimer = ref(null)

// 视频相关状态
const videoPlayer = ref(null)
const isPlaying = ref(false)
const isMuted = ref(false)
const isFullscreen = ref(false)
const isVideoLoading = ref(true)
const showVideoCover = ref(true)
const videoDuration = ref('00:00')

// 视频路径
const introVideoPath = '/uploads/init/recruitment_intro.mp4'

// 计算属性
const pageTitle = computed(() => {
  return pageData.value?.page_name || t('menu.honors')
})

const richtextBeforeVideo = computed(() => {
  const html = pageData.value?.content?.richtext || ''
  const firstH2EndIndex = html.indexOf('</h2>')
  if (firstH2EndIndex === -1) {
    return html
  }
  return html.substring(0, firstH2EndIndex + 5)
})

const richtextAfterVideo = computed(() => {
  const html = pageData.value?.content?.richtext || ''
  const firstH2EndIndex = html.indexOf('</h2>')
  if (firstH2EndIndex === -1) {
    return ''
  }
  return html.substring(firstH2EndIndex + 5)
})

const imageList = computed(() => {
  return pageData.value?.content?.image_list || []
})


// 视频控制方法
const playVideo = () => {
  if (videoPlayer.value) {
    videoPlayer.value.play()
    showVideoCover.value = false
  }
}

const pauseVideo = () => {
  if (videoPlayer.value) {
    videoPlayer.value.pause()
  }
}

const togglePlay = () => {
  if (!videoPlayer.value) return
  if (videoPlayer.value.paused) {
    playVideo()
  } else {
    pauseVideo()
  }
}

const toggleMute = () => {
  if (videoPlayer.value) {
    videoPlayer.value.muted = !videoPlayer.value.muted
    isMuted.value = videoPlayer.value.muted
  }
}

const restartVideo = () => {
  if (videoPlayer.value) {
    videoPlayer.value.currentTime = 0
    playVideo()
  }
}

const toggleFullscreen = () => {
  if (!videoPlayer.value) return
  if (!document.fullscreenElement) {
    videoPlayer.value.requestFullscreen?.()
      .then(() => {
        isFullscreen.value = true
      })
      .catch(err => {
        console.error('全屏模式失败:', err)
      })
  } else {
    document.exitFullscreen?.()
    isFullscreen.value = false
  }
}

// 视频事件处理
const onVideoLoaded = () => {
  isVideoLoading.value = false
  const duration = videoPlayer.value?.duration || 0
  const minutes = Math.floor(duration / 60)
  const seconds = Math.floor(duration % 60)
  videoDuration.value = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

const onVideoError = (event) => {
  console.error('视频加载失败:', event)
  isVideoLoading.value = false
}

const onVideoEnded = () => {
  isPlaying.value = false
}

const handlePlay = () => {
  isPlaying.value = true
}

const handlePause = () => {
  isPlaying.value = false
}

const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement
}


// 获取页面数据
const fetchPageData = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // 使用当前的语言
    const currentLocale = locale.value

    const response = await getRecruitmentIntro({lang: currentLocale})

    if (response && response.content) {
      pageData.value = response
    } else {
      pageData.value = null
    }
  } catch (error) {
    console.error('获取页面数据失败:', error)
    errorMessage.value = t('common.load_failed')
  } finally {
    isLoading.value = false
  }
}

// 监听语言变化，重新加载数据
watch(locale, () => {
  fetchPageData()
})

// 组件挂载时获取数据
onMounted(() => {
  fetchPageData()
  document.addEventListener('fullscreenchange', handleFullscreenChange)
})

// 组件卸载时清理定时器
onUnmounted(() => {
  clearInterval(autoPlayTimer.value)
  if (videoPlayer.value) {
    videoPlayer.value.pause()
    videoPlayer.value = null
  }
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
})

// 监听视频播放器引用
watch(videoPlayer, (newPlayer) => {
  if (newPlayer) {
    newPlayer.addEventListener('play', handlePlay)
    newPlayer.addEventListener('pause', handlePause)
  }
}, { immediate: true })
</script>

<style scoped lang="scss">
@import '@/assets/css/carousel';

:deep(.richtext-content){
  h1, h2{
    letter-spacing: 2px !important;
    margin: 20px 0 15px !important;
  }
}

.video-player-section {
  background: $white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);
  margin: 20px 0;
}

.video-player-container {
  width: 100%;
}

.video-wrapper {
  position: relative;
  width: 100%;
  background: #000;
  aspect-ratio: 16/9;
  overflow: hidden;
}

.company-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #000;
}

.video-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10;

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba($white, 0.1);
    border-top-color: $white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  .loading-text {
    color: $white;
    font-size: 1rem;
  }
}

.video-cover {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, rgba($primary-color, 0.9), rgba($primary-color-2, 0.8));
  cursor: pointer;
  z-index: 20;
  transition: opacity 0.3s ease;

  &:hover {
    .play-button {
      transform: scale(1.1);
    }
  }
}

.cover-content {
  text-align: center;
}

.play-button {
  margin-bottom: 20px;
  transition: transform 0.3s ease;
}

.play-hint {
  color: $white;
  font-size: 1.125rem;
  font-weight: 500;
  letter-spacing: 1px;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  z-index: 5;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  .video-overlay:hover & {
    opacity: 1;
  }
}

.play-button-overlay {
  width: 80px;
  height: 80px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
    background: rgba(0, 0, 0, 0.8);
  }
}

.video-info {
  padding: 20px 30px;
}

.video-meta {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: $text-light;
  font-size: 0.9375rem;

  .el-icon {
    color: $primary-color;
    font-size: 1.125rem;
  }
}

.control-buttons {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 12px;
  }
}

.control-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: $gray-10;
  border: 1px solid rgba($primary-color, 0.1);
  border-radius: 8px;
  color: $text-color;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  width: auto;

  .el-icon {
    font-size: 1.125rem;
    color: $primary-color;
  }

  &:hover {
    background: rgba($primary-color, 0.05);
    border-color: rgba($primary-color, 0.3);
    transform: translateY(-2px);
  }

  &.active {
    background: $primary-color;
    border-color: $primary-color;
    color: $white;

    .el-icon {
      color: $white;
    }
  }

  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 0.875rem;

    .el-icon {
      font-size: 1rem;
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .video-info {
    padding: 16px 20px;
  }
}

@media (max-width: 480px) {
  .video-info {
    padding: 12px 16px;
  }
}
</style>